import type { Topic, TopicId } from './types';

/**
 * Prereqs order the "Up next" recommendation so she is never handed a topic
 * that leans on something she has not touched. The artifact prototype just
 * picked the first untouched topic in array order.
 */
export const TOPICS: readonly Topic[] = [
  {
    id: 'frac',
    name: 'Fractions and decimals',
    blurb: 'Read the rule, then say it in decimals.',
    prereqs: [],
    bench:
      'Grab a rule. Measure three things on your bench and write each one twice: as a fraction and as a decimal.',
  },
  {
    id: 'inmm',
    name: 'Inches and millimetres',
    blurb: 'One number, 25.4, both directions.',
    prereqs: ['frac'],
    bench:
      'Find something with a metric callout on it. Convert it to inches, then check yourself with a caliper.',
  },
  {
    id: 'tenths',
    name: 'Thousandths and tenths',
    blurb: 'Where the decimal point earns its money.',
    prereqs: ['frac'],
    bench:
      'Mic the same spot on one part three times. Write all three readings. Notice how much they move.',
  },
  {
    id: 'tol',
    name: 'Tolerances and fits',
    blurb: 'Limits, clearance, and when parts fight.',
    prereqs: ['tenths'],
    bench:
      'Read a real print. Write down the limits for one toleranced dimension before you cut anything.',
  },
  {
    id: 'speeds',
    name: 'RPM, feed and SFM',
    blurb: 'Set the machine on purpose, not by ear.',
    prereqs: ['frac'],
    bench:
      'Pick a cutter off the shelf. Look up its SFM, work out the RPM, then compare it to what the machine is actually set at.',
  },
  {
    id: 'trig',
    name: 'Right-triangle trig',
    blurb: 'Sine, cosine, tangent, without the panic.',
    prereqs: ['frac'],
    bench:
      'Find any angled surface in the shop. Measure the rise and the run, then work out the angle before you look it up.',
  },
  {
    id: 'bolt',
    name: 'Bolt circles',
    blurb: 'Six holes, one circle, no guessing.',
    prereqs: ['trig'],
    bench:
      'Find a bolted flange. Count the holes, measure the bolt circle, and work out the degrees between holes.',
  },
  {
    id: 'taper',
    name: 'Tapers',
    blurb: 'Per inch, per foot, and the compound.',
    prereqs: ['trig'],
    bench:
      'Find a tapered shank. Measure both diameters and the length, then work out the taper per foot.',
  },
  {
    id: 'threads',
    name: 'Threads and tap drills',
    blurb: 'Pitch, TPI, and the hole that goes in first.',
    prereqs: ['frac'],
    bench:
      'Pick any bolt out of the bin. Work out its TPI with a thread gauge, then say what drill you would use to tap that size before you look it up.',
  },
  {
    id: 'print',
    name: 'Reading a print',
    blurb: 'Symbols, datums, and what the drawing is actually asking for.',
    prereqs: ['tol'],
    bench:
      'Take a real print off the rack. Find the datums, the title block tolerance, and one feature control frame. Say out loud what each one is asking you to hold.',
  },
  {
    id: 'stock',
    name: 'Stock, area and weight',
    blurb: 'How much material, and how heavy is it.',
    prereqs: ['frac'],
    bench:
      'Pick up a piece of bar stock. Guess its weight, then work it out from the size and material. See how close you were.',
  },
  {
    id: 'ratio',
    name: 'Ratios and gearing',
    blurb: 'Proportions, change gears, and scaling a job.',
    prereqs: ['frac'],
    bench:
      'Find any two gears or pulleys in the shop. Count the teeth or measure the diameters and work out the ratio between them.',
  },
  {
    id: 'sinebar',
    name: 'Sine bar setups',
    blurb: 'Turning an angle into a stack of blocks.',
    prereqs: ['trig'],
    bench:
      'Find a sine bar and a gauge block set. Pick an angle off a print and build the stack for it before you check the chart.',
  },
];

export const TOPIC_BY_ID: ReadonlyMap<TopicId, Topic> = new Map(TOPICS.map((t) => [t.id, t]));

export function getTopic(id: TopicId): Topic {
  const topic = TOPIC_BY_ID.get(id);
  if (!topic) throw new Error(`Unknown topic: ${id}`);
  return topic;
}
